<template>
    <AppLayout>
        <div class="container p-4 mx-auto md:px-1 md:py-4">
            <div class="overflow-hidden rounded-md">
                <div class="mb-4 text-xl font-semibold md:text-2xl">
                    KYC Form
                </div>

                <div class="p-2 space-y-6 bg-white lg:px-4">
                    <!-- Primary Details  -->
                    <div>
                        <div class="mx-2 text-xl font-semibold">
                            Primary  Details
                        </div>
                        <div class="flex flex-col items-start md:flex-row">
                            <!-- logo image -->
                            <div class="m-2">
                                <div class="block text-sm font-semibold text-gray-700">
                                    Logo*
                                </div>

                                <div
                                    class="cursor-pointer"
                                    @click="replaceImage('logo')"
                                >
                                    <img
                                        v-if="logoPreview"
                                        class="object-cover rounded w-60 h-60"
                                        :src="logoPreview"
                                        :alt="$page.props.user.name"
                                    >

                                    <div
                                        v-else
                                        class="relative group w-52 h-52"
                                    >
                                        <div class="relative w-full h-full font-medium text-green-600 bg-white rounded-md cursor-pointer hover:text-green-500">
                                            <div
                                                class="flex justify-center w-full h-full px-6 pt-5 pb-6 mt-2 rounded-md"
                                                :class="errors('logo') ? 'border-2 border-red-300 border-dashed' : 'border-2 border-gray-300 border-dashed'"
                                            >
                                                <div class="flex flex-col items-center justify-center space-y-1 text-center">
                                                    <FileUploadIcon />
                                                    <div class="flex items-center justify-center text-sm text-gray-600">
                                                        <span>Upload Logo</span>
                                                    </div>
                                                    <p class="text-xs text-gray-500">
                                                        PNG, JPG, GIF up to 5MB
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <input
                                        ref="logo-image-upload"
                                        class="hidden"
                                        type="file"
                                        @change="filesChange($event.target.files, 'logo')"
                                    >
                                </div>

                                <div
                                    v-if="errors('logo')"
                                    class="mt-4 text-red-500"
                                >
                                    {{ errors('logo') }}
                                </div>
                            </div>

                            <div class="grid w-full grid-cols-1 gap-5 m-2 lg:grid-cols-2">
                                <!-- person name -->
                                <div>
                                    <div class="block text-sm font-semibold text-gray-700">
                                        Person Name*
                                    </div>
                                    <div class="flex mt-1 rounded-md shadow-sm">
                                        <t-input
                                            v-model="form.personName"
                                            type="text"
                                            placeholder="Person Name"
                                            :variant="errors('person_name') ? 'danger' : ''"
                                        />
                                    </div>
                                    <div
                                        v-if="errors('person_name')"
                                        class="text-red-500"
                                    >
                                        {{ errors('person_name') }}
                                    </div>
                                </div>

                                <!-- Email -->
                                <div>
                                    <div class="block text-sm font-semibold text-gray-700">
                                        Email*
                                    </div>
                                    <div class="flex flex-col mt-1 rounded-md shadow-sm">
                                        <t-input
                                            v-model="form.email"
                                            type="email"
                                            placeholder="Email"
                                            :variant="errors('email') ? 'danger' : ''"
                                        />
                                        <div
                                            v-if="errors('email')"
                                            class="text-red-500"
                                        >
                                            {{ errors('email') }}
                                        </div>
                                    </div>
                                </div>

                                <!-- Phone -->
                                <div>
                                    <div class="block text-sm font-semibold text-gray-700">
                                        Phone*
                                    </div>
                                    <div class="flex flex-col mt-1 rounded-md shadow-sm">
                                        <t-input
                                            v-model="form.phone"
                                            type="number"
                                            placeholder="Phone"
                                            :variant="errors('phone') ? 'danger' : ''"
                                        />
                                        <div
                                            v-if="errors('phone')"
                                            class="text-red-500"
                                        >
                                            {{ errors('phone') }}
                                        </div>
                                    </div>
                                </div>

                                <!-- dob -->
                                <div>
                                    <div class="block text-sm font-semibold text-gray-700">
                                        DOB*
                                    </div>
                                    <div class="mt-1 rounded-md shadow-sm">
                                        <DatePicker
                                            v-model="form.dob"
                                            class="form-input"
                                            format="YYYY-MM-DD"
                                            :options="{ firstDay: 1 }"
                                        />
                                    </div>
                                    <div
                                        v-if="errors('dob')"
                                        class="text-red-500"
                                    >
                                        {{ errors('dob') }}
                                    </div>
                                </div>

                                <!-- gender -->
                                <div>
                                    <div class="block text-sm font-semibold text-gray-700">
                                        Gender*
                                    </div>
                                    <div class="-mt-1 rounded-md shadow-sm">
                                        <select
                                            v-model="form.gender"
                                            :variant="errors('gender') ? 'danger' : ''"
                                            class="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                                        >
                                            <option
                                                disabled
                                                value=""
                                            />
                                            <option value="male">
                                                Male
                                            </option>
                                            <option value="female">
                                                Female
                                            </option>
                                            <option value="other">
                                                Other
                                            </option>
                                        </select>
                                    </div>

                                    <div
                                        v-if="errors('gender')"
                                        class="text-red-500"
                                    >
                                        {{ errors('gender') }}
                                    </div>
                                </div>

                                <!-- role -->
                                <div>
                                    <div class="block text-sm font-semibold text-gray-700">
                                        Role*
                                    </div>
                                    <div class="-mt-1 rounded-md shadow-sm">
                                        <select
                                            v-model="form.role"
                                            :variant="errors('role') ? 'danger' : ''"
                                            class="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                                        >
                                            <option
                                                disabled
                                                value=""
                                            />
                                            <option 
                                                v-if="isSuperAdmin || isAdmin || isRetailer || (isWholesaler && kycVerified)"
                                                value="retailer">
                                                Retailer
                                            </option>
                                            <option
                                                v-if="isSuperAdmin || (isWholesaler && !kycVerified)"
                                                value="wholesaler"
                                            >
                                                Wholesaler
                                            </option>
                                        </select>
                                    </div>

                                    <div
                                        v-if="errors('role')"
                                        class="text-red-500"
                                    >
                                        {{ errors('role') }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Company Details  -->
                    <div>
                        <div class="mx-2 text-xl font-semibold">
                            Company Details
                        </div>

                        <div class="grid grid-cols-1 gap-5 m-2 md:grid-cols-2">
                            <!-- Company Name -->
                            <div>
                                <div class="block text-sm font-semibold text-gray-700">
                                    Company Name*
                                </div>
                                <div class="flex flex-col mt-1 rounded-md shadow-sm">
                                    <t-input
                                        v-model="form.companyName"
                                        type="text"
                                        placeholder="Company Name"
                                        :variant="errors('company_name') ? 'danger' : ''"
                                    />
                                    <div
                                        v-if="errors('company_name')"
                                        class="text-red-500"
                                    >
                                        {{ errors('company_name') }}
                                    </div>
                                </div>
                            </div>

                            <!-- Company Registration Number -->
                            <div>
                                <div class="block text-sm font-semibold text-gray-700">
                                    Company Registration Number*
                                </div>
                                <div class="flex flex-col mt-1 rounded-md shadow-sm">
                                    <t-input
                                        v-model="form.companyRegistrationNumber"
                                        type="text"
                                        placeholder="Company Registration Number"
                                        :variant="errors('company_registration_number') ? 'danger' : ''"
                                    />
                                    <div
                                        v-if="errors('company_registration_number')"
                                        class="text-red-500"
                                    >
                                        {{ errors('company_registration_number') }}
                                    </div>
                                </div>
                            </div>

                            <!-- Business address -->
                            <div>
                                <div class="block text-sm font-semibold text-gray-700">
                                    Business Address*
                                </div>
                                <div class="flex flex-col mt-1 rounded-md shadow-sm">
                                    <t-input
                                        v-model="form.businessAddress"
                                        type="text"
                                        placeholder="Business address"
                                        :variant="errors('business_address') ? 'danger' : ''"
                                    />
                                    <div
                                        v-if="errors('business_address')"
                                        class="text-red-500"
                                    >
                                        {{ errors('business_address') }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Bank Details  -->
                    <div>
                        <div class="mx-2 text-xl font-semibold">
                            Bank Details
                        </div>
                        <div class="grid grid-cols-1 gap-5 m-2 md:grid-cols-2">
                            <!-- Bankers Name -->
                            <div>
                                <div class="block text-sm font-semibold text-gray-700">
                                    Bankers Name*
                                </div>
                                <div class="flex flex-col mt-1 rounded-md shadow-sm">
                                    <t-input
                                        v-model="form.bankersName"
                                        type="text"
                                        placeholder="Bankers Name"
                                        :variant="errors('bankers_name') ? 'danger' : ''"
                                    />
                                    <div
                                        v-if="errors('bankers_name')"
                                        class="text-red-500"
                                    >
                                        {{ errors('bankers_name') }}
                                    </div>
                                </div>
                            </div>

                            <!-- Bankers address -->
                            <div>
                                <div class="block text-sm font-semibold text-gray-700">
                                    Bankers Address*
                                </div>
                                <div class="flex flex-col mt-1 rounded-md shadow-sm">
                                    <t-input
                                        v-model="form.bankersAddress"
                                        type="text"
                                        placeholder="Bankers address"
                                        :variant="errors('bankers_address') ? 'danger' : ''"
                                    />
                                    <div
                                        v-if="errors('bankers_address')"
                                        class="text-red-500"
                                    >
                                        {{ errors('bankers_address') }}
                                    </div>
                                </div>
                            </div>

                            <!-- Business References Name -->
                            <div>
                                <div class="block text-sm font-semibold text-gray-700">
                                    Business References Name*
                                </div>
                                <div class="flex flex-col mt-1 rounded-md shadow-sm">
                                    <t-input
                                        v-model="form.businessReferencesName"
                                        type="text"
                                        placeholder="Business References Name"
                                        :variant="errors('business_references_name') ? 'danger' : ''"
                                    />
                                    <div
                                        v-if="errors('business_references_name')"
                                        class="text-red-500"
                                    >
                                        {{ errors('business_references_name') }}
                                    </div>
                                </div>
                            </div>

                            <!-- Business References Address -->
                            <div>
                                <div class="block text-sm font-semibold text-gray-700">
                                    Business References Address*
                                </div>
                                <div class="flex flex-col mt-1 rounded-md shadow-sm">
                                    <t-input
                                        v-model="form.businessReferencesAddress"
                                        type="text"
                                        placeholder="Business References Address"
                                        :variant="errors('business_references_address') ? 'danger' : ''"
                                    />
                                    <div
                                        v-if="errors('business_references_address')"
                                        class="text-red-500"
                                    >
                                        {{ errors('business_references_address') }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Documents  -->
                    <div>
                        <div class="mx-2 text-xl font-semibold">
                            Documents*
                        </div>

                        <div class="grid grid-cols-1 gap-6 m-2 lg:grid-cols-3">
                            <!-- id_document -->
                            <div class="relative group">
                                <label class="text-sm font-semibold text-gray-700">
                                    <span>One Form ID *</span>
                                </label>

                                <div @click="replaceImage('id-document')">
                                    <img
                                        v-if="idDocumentPreview"
                                        class="object-cover object-center max-w-full rounded h-36"
                                        :src="idDocumentPreview"
                                    >

                                    <div
                                        v-else
                                        class="relative font-medium text-green-600 bg-white rounded-md cursor-pointer hover:text-green-500"
                                    >
                                        <div
                                            class="flex justify-center px-6 pt-5 pb-6 mt-2 rounded-md"
                                            :class="errors('id_document') ? 'border-2 border-red-300 border-dashed' : 'border-2 border-gray-300 border-dashed'"
                                        >
                                            <div class="flex flex-col items-center justify-center space-y-1 text-center">
                                                <FileUploadIcon />
                                                <div class="flex items-center justify-center text-sm text-gray-600">
                                                    <span>Upload One form of ID</span>
                                                </div>
                                                <p class="text-xs text-gray-500">
                                                    PNG, JPG, GIF up to 5MB
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- image input -->
                                    <input
                                        ref="id-document-image-upload"
                                        class="hidden"
                                        type="file"
                                        @change="filesChange($event.target.files, 'id-document')"
                                    >
                                </div>

                                <div
                                    v-if="errors('id_document')"
                                    class="text-red-500"
                                >
                                    {{ errors('id_document') }}
                                </div>
                            </div>

                            <!-- registration certificate -->
                            <div class="relative group">
                                <label class="text-sm font-semibold text-gray-700">
                                    <span>Business Registration Certificate *</span>
                                </label>
                                <div
                                    @click="replaceImage('registration-certificate')"
                                >
                                    <img
                                        v-if="registrationCertificatePreview"
                                        class="object-cover object-center max-w-full rounded h-36"
                                        :src="registrationCertificatePreview"
                                    >

                                    <div
                                        v-else
                                        class="relative font-medium text-green-600 bg-white rounded-md cursor-pointer hover:text-green-500"
                                    >
                                        <div
                                            class="flex justify-center px-6 pt-5 pb-6 mt-2 rounded-md"
                                            :class="errors('registration_certificate') ? 'border-2 border-red-300 border-dashed' : 'border-2 border-gray-300 border-dashed'"
                                        >
                                            <div class="flex flex-col items-center justify-center space-y-1 text-center">
                                                <FileUploadIcon />
                                                <div class="flex items-center justify-center text-sm text-gray-600">
                                                    <span>Upload Registration Certificate</span>
                                                </div>
                                                <p class="text-xs text-gray-500">
                                                    PNG, JPG, GIF up to 5MB
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- image input -->
                                    <input
                                        ref="registration-certificate-image-upload"
                                        class="hidden"
                                        type="file"
                                        @change="filesChange($event.target.files, 'registration-certificate')"
                                    >
                                </div>

                                <div
                                    v-if="errors('registration_certificate')"
                                    class="text-red-500"
                                >
                                    {{ errors('registration_certificate') }}
                                </div>
                            </div>

                            <!-- Vat certificate -->
                            <div v-if="isWholesaler">
                                <!-- accept_vat checkbox -->
                                <!-- <div>
                                    <div class="flex mt-1 rounded-md shadow-sm">
                                        <label class="flex items-center">
                                            <span class="ml-2">Accept Vat from Retailers</span>
                                        </label>
                                    </div>
                                </div> -->

                                <!-- vat-certificate -->
                                <div class="relative group">
                                    <label class="text-sm font-semibold text-gray-700">
                                        <t-checkbox v-model="form.acceptVat" />
                                        <span>Accept Vat</span>
                                    </label>
                                    <div
                                        @click="replaceImage('vat-registration')"
                                    >
                                        <img
                                            v-if="vatRegistrationCertificatePreview"
                                            class="object-cover object-center max-w-full rounded h-36"
                                            :src="vatRegistrationCertificatePreview"
                                        >

                                        <div
                                            v-else
                                            class="relative font-medium text-green-600 bg-white rounded-md hover:text-green-500"
                                            :class="form.acceptVat ? 'cursor-pointer' : 'cursor-default opacity-50'"
                                        >
                                            <div
                                                class="flex justify-center px-6 pt-5 pb-6 mt-2 rounded-md"
                                                :class="errors('vat_registration_certificate') ? 'border-2 border-red-300 border-dashed' : 'border-2 border-gray-300 border-dashed'"
                                            >
                                                <div class="flex flex-col items-center justify-center space-y-1 text-center">
                                                    <FileUploadIcon />
                                                    <div class="flex items-center justify-center text-sm text-gray-600">
                                                        <span>Upload Vat Certificate</span>
                                                    </div>
                                                    <p class="text-xs text-gray-500">
                                                        PNG, JPG, GIF up to 5MB
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- image input -->
                                        <input
                                            ref="vat-registration-image-upload"
                                            :disabled="form.acceptVat ? false : true"
                                            class="hidden"
                                            type="file"
                                            @change="filesChange($event.target.files, 'vat-registration')"
                                        >
                                    </div>
                                    <div
                                        v-if="errors('vat_registration_certificate')"
                                        class="mt-4 text-red-500"
                                    >
                                        {{ errors('vat_registration_certificate') }}
                                    </div>
                                </div>
                            </div>

                            <!-- file upload -->
                            <div class="relative group">
                                <label class="text-sm font-semibold text-gray-700">
                                    <span>Other Supportive Documents (Multiple)</span>
                                </label>
                                <FileUpload
                                    :multiple="true"
                                    @filesUploaded="attachFilesToForm"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="py-3 text-right">
                    <t-button
                        variant="success"
                        :disabled="processing"
                        @click="submitForm"
                    >
                        Save
                    </t-button>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script>
import AppLayout from '../../Layouts/AppLayout.vue';
import FileUpload from '../../Components/FileUpload.vue';
import DatePicker from '../../Components/DatePicker.vue';
import Kyc from '../../Models/Kyc';
import FileUploadIcon from '../../Components/Icons/FileUploadIcon.vue';

export default {
    name: 'KycCreate',

    components: {
        AppLayout,
        FileUpload,
        FileUploadIcon,
        DatePicker,
    },

    props: {
        kyc: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            form: new Kyc(),
            logoPreview: null,
            idDocumentPreview: null,
            registrationCertificatePreview: null,
            vatRegistrationCertificatePreview: null,
            processing: false,
        };
    },

    mounted() {
        if (this.kyc) {
            this.logoPreview = this.kyc.logo;
            this.form.personName = this.kyc.person_name;
            this.form.email = this.kyc.email;
            this.form.phone = this.kyc.phone;
            this.form.dob = this.kyc.dob;
            this.form.gender = this.kyc.gender;
            this.form.companyName = this.kyc.company_name;
            this.form.companyRegistrationNumber = this.kyc.company_registration_number;
            this.form.businessAddress = this.kyc.business_address;
            this.form.bankersName = this.kyc.bankers_name;
            this.form.bankersAddress = this.kyc.bankers_address;
            this.form.businessReferencesName = this.kyc.business_references_name;
            this.form.businessReferencesAddress = this.kyc.business_references_address;
            this.form.acceptVat = this.kyc.accept_vat;
            this.idDocumentPreview = this.kyc.id_document;
            this.registrationCertificatePreview = this.kyc.registration_certificate;
            // this.vatRegistrationCertificatePreview = this.kyc.vat_registration_certificate;
        }
    },

    methods: {
        submitForm() {
            const payload = this.buildPayload();
            this.save(payload);
        },

        buildPayload() {
            const formData = new FormData();
            if (this.form.logo) {
                formData.append('logo', this.form.logo);
            }
            if (this.form.personName) {
                formData.append('person_name', this.form.personName);
            }
            if (this.form.email) {
                formData.append('email', this.form.email);
            }
            if (this.form.phone) {
                formData.append('phone', this.form.phone);
            }
            if (this.form.dob) {
                formData.append('dob', this.form.dob);
            }
            if (this.form.gender) {
                formData.append('gender', this.form.gender);
            }
            if (this.form.role) {
                formData.append('role', this.form.role);
            }
            if (this.form.companyName) {
                formData.append('company_name', this.form.companyName);
            }
            if (this.form.companyRegistrationNumber) {
                formData.append('company_registration_number', this.form.companyRegistrationNumber);
            }
            if (this.form.businessAddress) {
                formData.append('business_address', this.form.businessAddress);
            }
            if (this.form.bankersName) {
                formData.append('bankers_name', this.form.bankersName);
            }
            if (this.form.bankersAddress) {
                formData.append('bankers_address', this.form.bankersAddress);
            }
            if (this.form.businessReferencesName) {
                formData.append('business_references_name', this.form.businessReferencesName);
            }
            if (this.form.businessReferencesAddress) {
                formData.append('business_references_address', this.form.businessReferencesAddress);
            }
            if (this.isWholesaler) {
                formData.append('accept_vat', this.form.acceptVat ? 1 : 0);
            }
            if (this.form.idDocument) {
                formData.append('id_document', this.form.idDocument);
            }
            if (this.form.registrationCertificate) {
                formData.append('registration_certificate', this.form.registrationCertificate);
            }
            if (this.form.vatRegistrationCertificate) {
                formData.append('vat_registration_certificate', this.form.vatRegistrationCertificate);
            }
            if (this.form.images) {
                for (let i = 0; i < this.form.images.length; i++) {
                    const file = this.form.images[i];
                    formData.append(`images[${i}]`, file);
                }
            }
            if (this.kyc) {
                // to send put request the param name should have been _method for laravel
                // but this param is being sent to the store method to let it know how to handle the data
                // we create new one if this param doesn't exist and update existing data otherwise in the same controller method
                formData.append('method', 'PUT');
            }
            return formData;
        },

        save(payload) {
            this.processing = true;
            this.$inertia.post(route('kyc.store'), payload, {
                onFinish: () => {
                    this.processing = false;
                },
            });
        },

        attachFilesToForm(files) {
            this.form.images = files;
        },

        filesChange(fileList, type) {
            if (!fileList.length) return;
            if (type === 'logo') {
                this.form.logo = fileList[0];
                this.getImagePreviews('logo');
            }
            if (type === 'id-document') {
                this.form.idDocument = fileList[0];
                this.getImagePreviews('idDocument');
            }
            if (type === 'registration-certificate') {
                this.form.registrationCertificate = fileList[0];
                this.getImagePreviews('registrationCertificate');
            }
            if (type === 'vat-registration') {
                this.form.vatRegistrationCertificate = fileList[0];
                this.getImagePreviews('vatRegistrationCertificate');
            }
        },

        getImagePreviews(attributeName) {
            if (/\.(jpe?g|png|gif)$/i.test(this.form[attributeName].name)) {
                const reader = new FileReader();
                reader.addEventListener('load', () => {
                    const dataAttributeName = `${attributeName}Preview`;
                    this[dataAttributeName] = reader.result;
                }, false);

                reader.readAsDataURL(this.form[attributeName]);
            }
        },

        replaceImage(type) {
            this.$refs[`${type}-image-upload`].click();
        },
    },
};
</script>
